import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <div className="container">
        <Link to="/" className="back-link">&larr; Back to Home</Link>
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: March 26, 2026</p>

        <section>
          <h2>Our Commitment to Privacy</h2>
          <p>
            10 Thirty is a secure communication platform built on the principle that private
            conversations should remain private. We collect the absolute minimum data necessary
            to provide our service, and we cannot read your messages or listen to your calls
            even if we wanted to.
          </p>
          <p>
            This policy is designed to comply with the Protection of Personal Information Act
            (POPIA) of South Africa and international data protection standards.
          </p>
        </section>

        <section>
          <h2>What We Collect</h2>
          <h3>Account Information</h3>
          <ul>
            <li><strong>Phone Number:</strong> Used to identify your account and enable contact connections. Stored in E.164 format.</li>
            <li><strong>Display Name:</strong> Optional name shown to your contacts.</li>
            <li><strong>Device Token:</strong> For push notifications. Deleted when you log out.</li>
          </ul>

          <h3>Communication Metadata</h3>
          <p>
            When you use 10 Thirty, we temporarily process the following metadata to deliver messages and facilitate calls:
          </p>
          <ul>
            <li>Who you communicate with (contact relationships)</li>
            <li>When tunnels are opened and closed</li>
            <li>Message content type (text, image, voice) and file sizes</li>
            <li>Call type (voice or video) and duration</li>
          </ul>
          <p>
            This metadata is deleted when the tunnel closes (maximum 10 minutes) or when messages expire (30 seconds after being read).
          </p>

          <h3>What We Never Collect</h3>
          <ul>
            <li><strong>Message Content:</strong> All messages are end-to-end encrypted. We cannot read them.</li>
            <li><strong>Call Audio/Video:</strong> Voice and video calls are peer-to-peer. Media never passes through our servers.</li>
            <li><strong>Message History:</strong> Messages are deleted 30 seconds after being read.</li>
            <li><strong>Conversation Logs:</strong> Tunnels close after 10 minutes. No records are kept.</li>
            <li><strong>Location Data:</strong> We do not track where you are.</li>
            <li><strong>Contact Lists:</strong> We do not upload your address book.</li>
          </ul>
        </section>

        <section>
          <h2>End-to-End Encryption</h2>
          <p>
            10 Thirty uses the Signal Protocol for end-to-end encryption of all messages. This means:
          </p>
          <ul>
            <li>Messages are encrypted on your device before sending</li>
            <li>Only the intended recipient can decrypt messages</li>
            <li>Our servers only see encrypted data that we cannot read</li>
            <li>Even with a court order, we cannot provide message content</li>
            <li>Media files (images, voice notes) are encrypted with AES-256-GCM before upload</li>
          </ul>
          <h3>Voice and Video Calls</h3>
          <p>
            Calls use WebRTC with DTLS-SRTP encryption. Audio and video data flows directly
            between devices (peer-to-peer) and never passes through our servers. Our server
            only relays the signaling data needed to establish the connection.
          </p>
          <h3>Safety Numbers</h3>
          <p>
            You can verify the identity of your contacts by comparing Safety Numbers.
            If both parties see the same number, the connection is secure. If a contact's
            identity key changes (e.g. they reinstalled the app), you will see a security
            warning and can re-verify.
          </p>
        </section>

        <section>
          <h2>On-Device Security</h2>
          <p>
            Data cached locally on your device is protected by multiple layers:
          </p>
          <ul>
            <li><strong>Encryption Keys:</strong> Stored in hardware-backed secure storage (iOS Keychain / Android Keystore)</li>
            <li><strong>Local Cache:</strong> All cached data (sent messages, media) is encrypted at rest with AES-256-GCM using a key that only exists in secure storage</li>
            <li><strong>Logout Protection:</strong> On logout or account deletion, the cache encryption key is permanently destroyed, making all residual data cryptographically unrecoverable</li>
            <li><strong>Screenshot Prevention:</strong> Screen capture is blocked throughout the app</li>
          </ul>
        </section>

        <section>
          <h2>Zero-Knowledge Architecture</h2>
          <p>
            10 Thirty operates on a zero-knowledge principle:
          </p>
          <ul>
            <li><strong>We don't hold the keys:</strong> Encryption keys are generated and stored exclusively on your device</li>
            <li><strong>We can't decrypt:</strong> We have no technical capability to read your messages or listen to your calls</li>
            <li><strong>We can't recover:</strong> Deleted messages are permanently gone — ciphertext is overwritten with empty data before deletion</li>
            <li><strong>We can't comply:</strong> We cannot provide message content in response to legal requests because we don't have access to it</li>
          </ul>
          <p>
            This architecture is by design. Your privacy is protected by mathematics, not policy.
          </p>
        </section>

        <section>
          <h2>Legal Requests</h2>
          <p>
            If we receive a valid legal request (subpoena, court order, etc.), we can only
            provide the limited account information we possess: phone number, display name,
            and account creation date. We cannot provide:
          </p>
          <ul>
            <li>Message content (encrypted, we don't have keys)</li>
            <li>Call recordings (peer-to-peer, never on our servers)</li>
            <li>Message history (deleted after 30 seconds)</li>
            <li>Conversation records (tunnels deleted after 10 minutes)</li>
            <li>Contact lists (not stored on our servers)</li>
          </ul>
        </section>

        <section>
          <h2>Data Retention</h2>
          <ul>
            <li><strong>Messages:</strong> Deleted 30 seconds after being read. Ciphertext wiped to empty bytes before removal.</li>
            <li><strong>Media:</strong> Encrypted files deleted from storage when messages expire or tunnels close.</li>
            <li><strong>Tunnels:</strong> Automatically closed and deleted after 10 minutes. Unread messages auto-delete after 24 hours.</li>
            <li><strong>Calls:</strong> No recordings stored. Signaling data is ephemeral and not logged.</li>
            <li><strong>Account Data:</strong> Retained until you delete your account.</li>
            <li><strong>Encryption Keys:</strong> Stored only on your device, never on our servers.</li>
          </ul>
        </section>

        <section>
          <h2>Third-Party Services</h2>
          <ul>
            <li><strong>Twilio:</strong> SMS verification codes and TURN server relay for call connectivity (no message content)</li>
            <li><strong>Firebase Cloud Messaging:</strong> Push notifications (device tokens only, no message content in notifications)</li>
            <li><strong>Firebase Storage:</strong> Temporary encrypted media storage (encrypted blobs only, deleted on message expiry)</li>
          </ul>
        </section>

        <section>
          <h2>POPIA Compliance</h2>
          <p>
            As a South African company, we comply with the Protection of Personal Information Act (POPIA):
          </p>
          <ul>
            <li><strong>Lawful Processing:</strong> We process personal information only for communication functionality with your explicit consent</li>
            <li><strong>Purpose Limitation:</strong> Your data is used solely to provide the communication service</li>
            <li><strong>Minimality:</strong> We collect only phone number and optional display name</li>
            <li><strong>Security Safeguards:</strong> End-to-end encryption, hardware-backed key storage, encrypted local cache</li>
            <li><strong>Data Subject Rights:</strong> You can access, correct, or delete your personal information at any time</li>
          </ul>
        </section>

        <section>
          <h2>Your Rights</h2>
          <p>Under POPIA and applicable data protection laws, you have the right to:</p>
          <ul>
            <li>Access your personal information</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Object to the processing of your personal information</li>
            <li>Delete your account at any time from within the app</li>
            <li>Opt out of push notifications</li>
          </ul>
          <p>
            To exercise these rights, contact us at{' '}
            <a href="mailto:privacy@10thirty.co.za">privacy@10thirty.co.za</a>
          </p>
        </section>

        <section>
          <h2>Push Notifications</h2>
          <p>
            Push notifications never contain message content. They only indicate that you
            have a new message or call request, along with the sender's name. No plaintext
            message content is ever included in notification payloads.
          </p>
        </section>

        <section>
          <h2>Contact</h2>
          <p>
            For privacy-related questions or to exercise your data subject rights, contact us at{' '}
            <a href="mailto:privacy@10thirty.co.za">privacy@10thirty.co.za</a>
          </p>
          <p>
            Information Officer: Call IT Services (Pty) Ltd
          </p>
        </section>

        <footer className="legal-footer">
          <div className="links">
            <Link to="/">Home</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
